import { useState } from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { Lock } from "lucide-react";
import { getCardDesign, type CardVariant } from "@/lib/card-designs";

interface CardVisualProps {
  cardNumber: string; // Last 4 digits or full number
  cardHolder?: string;
  expMonth?: number;
  expYear?: number;
  cvv?: string;
  balance?: number; // Balance in cents
  status: string;
  nickname?: string;
  cardType?: string; // Card brand: VISA, MASTERCARD, RUPAY
  variant?: CardVariant;
  className?: string;
  size?: "sm" | "md" | "lg";
  interactive?: boolean;
  showFullNumber?: boolean;
}

export function CardVisual({
  cardNumber,
  cardHolder,
  expMonth,
  expYear,
  cvv,
  status,
  cardType,
  variant = 1,
  className,
  size = "md",
  interactive = true,
}: CardVisualProps) {
  const [isHovered, setIsHovered] = useState(false);

  const sizeClasses = {
    sm: "w-[280px] h-[176px]",
    md: "w-[324px] h-[204px]",
    lg: "w-[405px] h-[255px]",
  };

  const textScale = {
    sm: { number: "text-sm", label: "text-[7px]", detail: "text-[10px]", brand: "text-base", pad: "p-3", gap: "space-y-1.5", brandLogo: "w-4 h-4" },
    md: { number: "text-lg", label: "text-[8px]", detail: "text-xs", brand: "text-lg", pad: "p-4", gap: "space-y-2", brandLogo: "w-5 h-5" },
    lg: { number: "text-xl", label: "text-[9px]", detail: "text-sm", brand: "text-xl", pad: "p-5", gap: "space-y-3", brandLogo: "w-6 h-6" },
  };

  const scale = textScale[size];

  const isInactive = status === "FROZEN" || status === "CANCELLED" || status === "EXPIRED";

  const formatExpiry = () => {
    if (expMonth && expYear) {
      const month = expMonth.toString().padStart(2, "0");
      const year = expYear.toString().slice(-2);
      return `${month}/${year}`;
    }
    return "••/••";
  };

  const maskedNumber = `•••• •••• •••• ${cardNumber || "••••"}`;
  const brandName = cardType?.toUpperCase() || "";

  const design = getCardDesign(variant);
  const frontImage = design.front;
  const backImage = design.back;

  return (
    <div
      className={cn("cursor-pointer", className)}
      style={{ perspective: "1000px" }}
      onMouseEnter={() => interactive && setIsHovered(true)}
      onMouseLeave={() => interactive && setIsHovered(false)}
    >
      <motion.div
        className={cn(
          "relative",
          sizeClasses[size],
          "transform-gpu"
        )}
        initial={false}
        animate={{ rotateY: isHovered ? 180 : 0 }}
        transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
        style={{
          transformStyle: "preserve-3d",
        }}
      >
        {/* Front of card */}
        <div
          className={cn(
            "absolute inset-0 overflow-hidden rounded-xl shadow-2xl",
            "transform-gpu transition-shadow duration-300",
            interactive && "hover:shadow-[0_20px_50px_rgba(0,0,0,0.3)]",
            isInactive && "grayscale opacity-70"
          )}
          style={{
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
          }}
        >
          {/* Front Card Image */}
          <img
            src={frontImage}
            alt="Card Front"
            className="absolute inset-0 w-full h-full object-cover"
          />

          {/* Frosted overlay for frozen/cancelled cards */}
          {isInactive && (
            <div className="absolute inset-0 bg-white/10 backdrop-blur-[1px]" />
          )}

          {/* Front overlay - only card network logo + status badge */}
          <div className={cn("absolute inset-0 flex flex-col justify-between text-white", scale.pad)}>
            {/* Status badge - top right */}
            {status && isInactive && (
              <div className="flex justify-end">
                <div className={cn(
                  "px-1.5 py-0.5 rounded-full text-[8px] font-bold uppercase tracking-wide backdrop-blur-sm border flex items-center gap-0.5",
                  status === "FROZEN" && "bg-blue-500/30 border-blue-400/30 text-blue-200",
                  status === "CANCELLED" && "bg-red-500/30 border-red-400/30 text-red-200",
                  status === "EXPIRED" && "bg-gray-500/30 border-gray-400/30 text-gray-200"
                )}>
                  {status === "FROZEN" && <Lock className="h-2.5 w-2.5" />}
                  {status}
                </div>
              </div>
            )}

            <div />

            {/* Card network logo - bottom right */}
            <div className="flex justify-end">
              <div className="flex items-center gap-1 opacity-90">
                {(brandName === "VISA" || (!brandName && !cardType)) && (
                  <span className={cn(scale.brand, "font-bold italic tracking-tight drop-shadow-[0_1px_3px_rgba(0,0,0,0.8)]")}>
                    VISA
                  </span>
                )}
                {brandName === "MASTERCARD" && (
                  <div className="flex -space-x-1.5">
                    <div className={cn(scale.brandLogo, "rounded-full bg-red-500/90 shadow-md")} />
                    <div className={cn(scale.brandLogo, "rounded-full bg-amber-400/90 shadow-md")} />
                  </div>
                )}
                {brandName === "RUPAY" && (
                  <span className={cn(size === "sm" ? "text-sm" : scale.brand, "font-bold drop-shadow-[0_1px_3px_rgba(0,0,0,0.8)]")}>
                    RuPay
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Back of card */}
        <div
          className={cn(
            "absolute inset-0 overflow-hidden rounded-xl shadow-2xl",
            "transform-gpu transition-shadow duration-300",
            interactive && "hover:shadow-[0_20px_50px_rgba(0,0,0,0.3)]",
            isInactive && "grayscale opacity-70"
          )}
          style={{
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
          }}
        >
          {/* Back Card Image */}
          <img
            src={backImage}
            alt="Card Back"
            className="absolute inset-0 w-full h-full object-cover"
          />

          {/* Frosted overlay for frozen cards */}
          {isInactive && (
            <div className="absolute inset-0 bg-white/10 backdrop-blur-[1px]" />
          )}

          {/* Back card content overlay - card details */}
          <div className={cn("absolute inset-0 flex flex-col justify-end text-white", scale.pad)}>
            <div className={scale.gap}>
              {/* Card number */}
              <div className={cn("font-mono tracking-[0.12em] font-medium drop-shadow-[0_1px_3px_rgba(0,0,0,0.8)]", scale.number)}>
                {maskedNumber}
              </div>

              {/* Name, expiry, and CVV */}
              <div className="flex justify-between items-end">
                <div className="space-y-0">
                  <div className={cn(scale.label, "uppercase tracking-[0.2em] text-white/70 font-medium drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]")}>
                    Card Holder
                  </div>
                  <div className={cn(scale.detail, "font-semibold uppercase tracking-wide truncate max-w-[120px] drop-shadow-[0_1px_3px_rgba(0,0,0,0.8)]")}>
                    {cardHolder || "CARDHOLDER"}
                  </div>
                </div>

                <div className="text-right space-y-0">
                  <div className={cn(scale.label, "uppercase tracking-[0.2em] text-white/70 font-medium drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]")}>
                    Valid Thru
                  </div>
                  <div className={cn("font-mono font-semibold drop-shadow-[0_1px_3px_rgba(0,0,0,0.8)]", scale.detail)}>
                    {formatExpiry()}
                  </div>
                </div>

                {cvv && (
                  <div className="text-right space-y-0">
                    <div className={cn(scale.label, "uppercase tracking-[0.2em] text-white/70 font-medium drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]")}>
                      CVV
                    </div>
                    <div className={cn("font-mono font-semibold drop-shadow-[0_1px_3px_rgba(0,0,0,0.8)]", scale.detail)}>
                      {cvv}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
